import { createContext } from 'react'
import type {
  CreateOgsmActualInput,
  CreateOgsmNodeInput,
  CreateOgsmRevisionInput,
  OgsmActual,
  OgsmArchiveResult,
  OgsmAssignee,
  OgsmBoard,
  OgsmNode,
  OgsmRemovalImpact,
  OgsmSession,
  OgsmSessionList,
  UpdateOgsmNodeInput,
} from '@/types/api'

/** Where a board's data comes from: the internal company routes or the portal's own. */
export interface OgsmApi {
  /** Root of every OGSM query key in this scope (company: ogsmKeys.company(id); portal: ['portal', 'ogsm']). */
  key: readonly unknown[]
  /** Also refreshed after node, actual and revision changes; null where there is no Scorecard (portal). */
  scorecardKey: readonly unknown[] | null
  /** False where no metric source exists (portal, G2). Hides metric linking; never an edit right. */
  metricLinking: boolean
  listSessions: () => Promise<OgsmSessionList>
  createSession: () => Promise<OgsmSession>
  getBoard: (sessionId: string) => Promise<OgsmBoard>
  updateStatement: (
    sessionId: string,
    statement: string | null
  ) => Promise<OgsmSession>
  createNode: (
    sessionId: string,
    input: CreateOgsmNodeInput
  ) => Promise<OgsmNode>
  updateNode: (nodeId: string, input: UpdateOgsmNodeInput) => Promise<OgsmNode>
  deleteNode: (nodeId: string) => Promise<void>
  getRemovalImpact: (nodeId: string) => Promise<OgsmRemovalImpact>
  archiveNode: (nodeId: string) => Promise<OgsmArchiveResult>
  moveNode: (nodeId: string, position: number) => Promise<OgsmNode>
  listAssignees: () => Promise<OgsmAssignee[]>
  listActuals: (nodeId: string) => Promise<OgsmActual[]>
  addActual: (
    nodeId: string,
    input: CreateOgsmActualInput
  ) => Promise<OgsmActual>
  reviseTarget: (
    nodeId: string,
    input: CreateOgsmRevisionInput
  ) => Promise<OgsmNode>
}

/** Null outside a provider: the internal company scope is used (useOgsmApi). The portal always provides. */
export const OgsmApiContext = createContext<OgsmApi | null>(null)
